import Link from "next/link";
import type { GetStaticProps } from "next";

import { allWordpressPosts } from "contentlayer/generated";

import Seo from "../../components/Seo";
import { parseGmtDate } from "../../lib/posts";

type ArchivePost = { title: string; href: string; publishedAt: string };
type BlogArchivePageProps = { publishedPosts: ArchivePost[] };

export default function BlogArchivePage({ publishedPosts }: BlogArchivePageProps) {
  const title = "รวมบทความทั้งหมด";

  return (
    <>
      <Seo
        title={title}
        description="รวมทุกบทความจาก KMCP Accounting จัดเรียงตามลำดับเวลาเพื่อให้คุณค้นหาความรู้ได้ง่าย."
        path="/blog/archive"
      />
      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8">
        <div className="flex flex-col gap-4">
          <h1 className="mt-6 text-4xl font-semibold text-gray-900">{title}</h1>
          <p className="max-w-2xl text-base text-gray-600">
            ค้นหาหัวข้อที่สนใจจากบทความ KMCP Accounting ทั้งหมด ทั้งเรื่องบัญชี ภาษี การบริหาร และการจดทะเบียนธุรกิจ
          </p>
          <Link href="/blog" className="text-sm font-semibold text-brand hover:underline">
            ← กลับไปหน้าบทความล่าสุด
          </Link>
        </div>
        {publishedPosts.length === 0 ? (
          <p className="mt-16 text-base text-gray-600">ยังไม่มีบทความเผยแพร่ในขณะนี้</p>
        ) : (
          <>
          <h2 className="sr-only">รายการบทความทั้งหมด</h2>
          <div className="mt-12 divide-y divide-slate-200 border-y border-slate-200">
            {publishedPosts.map((post) => (
              <article key={post.href} className="grid gap-2 py-5 sm:grid-cols-[9rem,1fr] sm:gap-6">
                <time dateTime={post.publishedAt} className="text-sm leading-7 text-slate-500">
                  {new Intl.DateTimeFormat("th-TH", { day: "numeric", month: "short", year: "numeric" }).format(new Date(post.publishedAt))}
                </time>
                <h3 className="text-lg font-semibold leading-7 text-gray-950">
                  <Link href={post.href} className="hover:text-[#806500] hover:underline">
                    {post.title}
                  </Link>
                </h3>
              </article>
            ))}
          </div>
          </>
        )}
      </section>
    </>
  );
}

export const getStaticProps: GetStaticProps<BlogArchivePageProps> = async () => {
  const publishedPosts = allWordpressPosts
    .filter((post) => post.status === "publish")
    .sort((a, b) => parseGmtDate(b.dateGmt || b.date).getTime() - parseGmtDate(a.dateGmt || a.date).getTime())
    .map((post) => ({
      title: post.title,
      href: `/blog/${post.seoSlug ?? post.slug}`,
      publishedAt: parseGmtDate(post.dateGmt || post.date).toISOString()
    }));

  return { props: { publishedPosts } };
};
